import { UIChunkType } from '@myre/app-agent-domain';
import { type UiLibI18n } from '@myre/domain-i18n-wordings';
import { ButtonComponent } from '@myre/ui-primitive-components';

import {
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { isDataUIPart } from 'ai';

import { WORDINGS } from '../../../myre-wordings-loader';

import {
  type ClarificationAnswer,
  type ClarificationQuestion,
  type MyreUiMessagePart,
  type QuestionOption,
} from '../../interfaces/chat.interfaces';

const FREE_TEXT_ID = '__free_text__';

/**
 * Renders a clarification question (from the generate_question tool) as selectable
 * options, ALWAYS with a free-text answer. Picking an option or submitting free text
 * emits `optionSelected`; the answer RESUMES the suspended run via the `resume`
 * channel (the SAME run continues — it is not a new chat turn).
 */
@Component({
  selector: 'app-question-options',
  standalone: true,
  imports: [ButtonComponent],
  templateUrl: './question-options.component.html',
  styleUrl: './question-options.component.scss',
})
export class QuestionOptionsComponent {
  public readonly part = input.required<MyreUiMessagePart>();
  public readonly streaming = input<boolean>(false);

  /** Emits the answer when the user picks an option or submits free text. */
  public readonly optionSelected = output<ClarificationAnswer>();

  protected readonly freeTextId = FREE_TEXT_ID;
  protected readonly _selectedId = signal<string | null>(null);
  protected readonly _freeText = signal('');

  private readonly _wordings = inject<UiLibI18n>(WORDINGS);

  protected get _w() {
    return this._wordings._library.chat;
  }

  protected readonly _question = computed<ClarificationQuestion | null>(() => {
    const part = this.part();

    if (!isDataUIPart(part) || part.type !== UIChunkType.DataQuestion) {
      return null;
    }

    return (part.data as { question?: ClarificationQuestion }).question ?? null;
  });

  /**
   * The option to highlight: a live pick (`_selectedId`) wins; otherwise, on a
   * reload, the persisted `answer` resolves back to the option whose resolved
   * value produced it (the same `value ?? label` expression `_onSelect` emitted),
   * or to the free-text row when nothing matches. Null while pending.
   */
  protected readonly _resolvedSelectedId = computed<string | null>(() => {
    const live = this._selectedId();

    if (live !== null) {
      return live;
    }

    const question = this._question();
    const answer = question?.answer;

    if (answer === undefined) {
      return null;
    }

    const matched = question?.options.find(
      option => (option.value ?? option.label) === answer,
    );

    return matched?.id ?? FREE_TEXT_ID;
  });

  /** The free-text to show pre-filled on a reload of a free-text answer. */
  protected readonly _resolvedFreeText = computed<string>(() => {
    const live = this._freeText();

    if (live) {
      return live;
    }

    if (this._resolvedSelectedId() === FREE_TEXT_ID) {
      return this._question()?.answer ?? '';
    }

    return '';
  });

  protected get _answered(): boolean {
    return this._resolvedSelectedId() !== null;
  }

  protected get _locked(): boolean {
    return this.streaming() || this._answered;
  }

  protected _onSelect(option: QuestionOption): void {
    if (this._locked) {
      return;
    }

    this._selectedId.set(option.id);
    // Send the agent-authored resolved value verbatim (never the bare label,
    // which the agent would have to re-resolve), but show the label in the bubble.
    this.optionSelected.emit({
      value: option.value ?? option.label,
      label: option.label,
    });
  }

  protected _onSubmitFreeText(): void {
    const text = this._freeText().trim();

    if (!text || this._locked) {
      return;
    }

    this._selectedId.set(FREE_TEXT_ID);
    this.optionSelected.emit({ value: text, label: text });
  }
}
